'use strict';

import { findContinueDraft, nextPayAppDisplayNumber, payAppNumberLabel } from './payAppDrafts.js';

function formatCurrency(n) {
  const v = typeof n === 'number' ? n : parseFloat(String(n ?? '0'));
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function billingProgressForProject(project) {
  const contract = parseFloat(project.original_contract ?? '0') || 0;
  const cos = parseFloat(project.change_order_total ?? '0') || 0;
  const billed = parseFloat(project.total_billed ?? '0') || 0;
  const retained = parseFloat(project.retention_held ?? '0') || 0;
  const contractTotal = contract + cos;
  const billedGross = billed + retained;
  const remaining = Math.max(0, contractTotal - billedGross);
  const pctBilled = contractTotal > 0
    ? Math.round((billedGross / contractTotal) * 1000) / 10
    : 0;
  return { contractTotal, billedGross, remaining, pctBilled };
}

function leftToBillSub(project) {
  const { remaining, pctBilled } = billingProgressForProject(project);
  const left = formatCurrency(remaining);
  return `${left} left to bill · ${pctBilled}% billed`;
}

function payAppCtaModel(opts) {
  const { project, payApps, retentionHeld, hasRetainageRelease } = opts;
  if (!project || project.completed_at || project.status === 'completed') {
    return { variant: 'hidden', primary: '', secondary: '', ariaLabel: '', draftPayAppId: null };
  }

  const subNormal = leftToBillSub(project);
  const subRetention = '$0 left to bill · 100% billed';
  const draft = findContinueDraft(payApps);

  if (draft) {
    const label = payAppNumberLabel(draft.app_number);
    const primary = label === 'Draft'
      ? 'Continue pay app · Draft'
      : `Continue pay app #${label} · draft`;
    return {
      variant: 'draft',
      primary,
      secondary: subNormal,
      ariaLabel: `${primary} ${subNormal}`,
      draftPayAppId: draft.id,
    };
  }

  const { remaining } = billingProgressForProject(project);
  if (remaining <= 0 && retentionHeld > 0 && !hasRetainageRelease) {
    const held = formatCurrency(retentionHeld);
    const primary = `Bill retention · ${held}`;
    return {
      variant: 'retention',
      primary,
      secondary: subRetention,
      ariaLabel: `${primary} ${subRetention}`,
      draftPayAppId: null,
    };
  }

  const nextN = nextPayAppDisplayNumber(payApps);
  const primary = `New pay app #${nextN}`;
  return {
    variant: 'new',
    primary,
    secondary: subNormal,
    ariaLabel: `${primary} ${subNormal}`,
    draftPayAppId: null,
  };
}

export { payAppCtaModel };
